/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { createStyles, Theme, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import React, { FC } from "react";
import { useSelector } from "react-redux";
import { Route, RouteComponentProps, Switch, useRouteMatch } from "react-router";
import { Link } from "react-router-dom";
import { AppState } from "../../redux";
import FollowUpEditor from "../components/FollowUpEditor";
import FollowUpFacilities from "../components/FollowUpFacilities";
import FollowUpFacilityView from "../components/FollowUpFacilityView";

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    root: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      zIndex: 0,
      [theme.breakpoints.up("xs")]: {
        overflowY: "auto",
      },
      WebkitOverflowScrolling: "touch",
    },
    container: {
      flex: 1,
      padding: 0,
      display: "flex",
      flexDirection: "column",
    },
    toolbar: {
      display: "flex",
      alignItems: "center",
      margin: theme.spacing(4, 0),
      position: "relative",
    },
    breadcrumbs: {
      // backgroundColor: theme.palette.background.paper,
      backgroundColor: theme.palette.primary.light,
      padding: theme.spacing(2, 4),
      flexDirection: 'row',
      boxShadow: theme.shadows[1],
      color: theme.palette.getContrastText(theme.palette.primary.main)
    },
    clearAppBar: {
      [theme.breakpoints.only("xs")]: {
        marginTop: "56px"
      }
    },
    breadcrumb: {
      color: "inherit",
      ...theme.typography.caption,
      margin: theme.spacing(0, 2),
      textDecoration: 'none',
      "&:hover": {
        color: theme.palette.grey[300],
      }
    },

    buttonGroupContainer: {
      display: "flex",
      flex: 1,
      justifyContent: "center",
    },
    modeButton: {
      textTransform: "none",
    },
    modeButtonRoot: {
      height: "32px",
      "&.Mui-selected": {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.getContrastText(theme.palette.primary.main),
        "&:hover": {
          backgroundColor: theme.palette.primary.main,
        },
      },
    },
    list: {
      marginBottom: theme.spacing(10),
      display: "flex",
      flexDirection: "column",
    },
    loading: {
      backgroundColor: "#fff",
    },
    pagination: {},
  });
});

interface FollowUpTabProperties extends RouteComponentProps <{
  facilityId?: string;
  planId?: string;
 }> {}

const FollowUpTab: FC<FollowUpTabProperties> = props => {
  const classes = useStyles(props);

  const followUps = useSelector((state: AppState) => state.followUps.items);
  const facilities = useSelector((state: AppState) => state.facilities.items);

  /** account for header switching between fixed and relative on small screens based on route. TODO only use one header positioning strategy*/
  const planView = useRouteMatch<{ facilityId: string }>("/follow-ups/:facilityId/:planId");

  return (
    <div className={classes.root}>
      <div id="breadcrumbs" className={`${classes.breadcrumbs} ${planView ? classes.clearAppBar : ''}`}>
        <Link to="/follow-ups" className={classes.breadcrumb}>
          Follow-Ups
            </Link>
        <Route path="/follow-ups/:facilityId"
          render={p => {
            const facilityId = p.match.params.facilityId

            const facility = facilities[facilityId];
            return facility ? (
              <>
                <Typography variant="caption">{'>'}</Typography>
                <Link to={`/follow-ups/${facilityId}`} className={classes.breadcrumb}>
                  {facility.name}
                </Link>
                <Route path="/follow-ups/:facilityId/:planId"
                  render={p => {
                    const planId = p.match.params.planId;

                    const plan = followUps.get(facilityId)?.find(fu => fu.data.id === planId);
                    return plan && (
                      <>
                        <Typography variant="caption">{'>'}</Typography>
                        <span className={classes.breadcrumb}>
                          {plan!.data.title}
                        </span>
                      </>
                    )
                  }}
                />
              </>
            ) : null
          }}
        />
      </div>

      <Switch>
        <Route path={`${props.match.path}/:facilityId/:planId`} component={FollowUpEditor} />
        <Route path={`${props.match.path}/:facilityId`} component={FollowUpFacilityView} />
        <Route path={props.match.path} component={FollowUpFacilities} />
      </Switch>

    </div>
  );
};

export default FollowUpTab;
